import { useRef, useMemo } from "react";

import { useCachedApi, generateCacheKey } from "./cache";
import {
  ProctorClient,
  TaxonomyClient,
  type ExamStatus,
  type ExamPillar,
  type ExamSection,
  type PreviousScore,
  type TopicScore,
  type UserExam,
  type UserExamResponse,
  type ExamPillarsResponse,
  type ExamSections,
  type ExamResult,
} from "../client/svcProctor";

import { getCurrentSelectedTenant } from "../utils";
import { sortExamSections } from "../atlas/utils/examSections";

// --- Data structure types ---

/** Score summary for a single completed exam */
export type ExamScoreSummary = {
  examInstanceId: string;
  scorePercent: number | null;
  completedAt: string | null;
};

/** Per-section breakdown of exams and scores */
export type SectionExamData = {
  /** Taxonomy section metadata */
  section: ExamSection & { path: string };
  /** First in-progress exam for this section, or null */
  inProgressExam: UserExam | null;
  /** Overall score (%) for the most recent completed exam, or null */
  latestScorePercent: number | null;
  /** Latest team average score (%) for this section, or null */
  latestTeamAveragePercent: number | null;
  /** Number of times the learner has taken an exam for this section */
  timesTaken: number;
  /** Historical scores for every completed exam in this section, newest first */
  scores: PreviousScore[];
  /** Topic-level score breakdown from the most recent exam */
  topicScores: TopicScore[];
  /** Per-question results from the most recent exam (if requested) */
  questionResults: ExamResult["results"] | null;
  /** Timestamp of when the latest score was completed */
  latestScoreCompletedAt: string | null;
};

/** Top-level data returned by useUserExams */
export type UserExamsData = {
  /** All sections with associated exam & score data */
  sections: SectionExamData[];
  /** Convenience: first in-progress exam found across any section */
  activeExam: {
    exam: UserExam;
    // Always taken from the path-filtered section list, so path is guaranteed
    section: ExamSection & { path: string };
  } | null;
};

/**
 * Fetch and cache taxonomy for exam sections
 */
export const useExamSections = (examKeys: string[]) => {
  const taxonomyClientRef = useRef<TaxonomyClient | null>(null);
  if (!taxonomyClientRef.current) {
    taxonomyClientRef.current = new TaxonomyClient();
  }

  const tenant = getCurrentSelectedTenant() ?? "";

  return useCachedApi<ExamSections>(
    generateCacheKey("listExamSections", examKeys, tenant),
    () => taxonomyClientRef.current!.listSections(examKeys, tenant),
    {
      refetchOnMount: true,
    }
  );
};

/**
 * Fetch and cache user exams, taxonomy sections, and pillar scores.
 * Returns a structured object that maps sections to their exams and scores.
 * Includes both user-initiated and assigned exams. Assigned exams that cover
 * multiple sections are split so each section gets the relevant results.
 */
export const useUserExams = ({
  enabled,
  includeStatuses,
  includeQuestionResults = false,
  excludeAssignments,
}: {
  enabled?: boolean;
  includeStatuses?: ExamStatus[];
  includeQuestionResults?: boolean;
  excludeAssignments?: boolean;
} = {}) => {
  const proctorClientRef = useRef<ProctorClient | null>(null);
  const taxonomyClientRef = useRef<TaxonomyClient | null>(null);

  if (!proctorClientRef.current) {
    proctorClientRef.current = new ProctorClient();
  }
  if (!taxonomyClientRef.current) {
    taxonomyClientRef.current = new TaxonomyClient();
  }

  const tenant = getCurrentSelectedTenant() ?? "";

  const {
    data: sections,
    isLoading: sectionsLoading,
    error: sectionsError,
    refetch: sectionsRefetch,
    isFetching: sectionsFetching,
  } = useCachedApi<ExamSections>(
    generateCacheKey("listExamSections", tenant),
    () => taxonomyClientRef.current!.listSections(["the-exam"], tenant),
    {
      refetchOnMount: true,
    }
  );

  const {
    data: examsResponse,
    isLoading: examsLoading,
    error: examsError,
    refetch: examsRefetch,
    isFetching: examsFetching,
  } = useCachedApi<UserExamResponse>(
    generateCacheKey("fetchUserExams", tenant, includeStatuses, excludeAssignments),
    () =>
      proctorClientRef.current!.fetchUserExams({
        includeStatuses,
        excludeAssignments,
      }),
    {
      refetchOnMount: true,
      enabled,
    }
  );

  const {
    data: pillarsResponse,
    isLoading: pillarsLoading,
    error: pillarsError,
    refetch: pillarsRefetch,
    isFetching: pillarsFetching,
  } = useCachedApi<ExamPillarsResponse>(
    generateCacheKey("fetchExamPillars", tenant, includeQuestionResults, excludeAssignments),
    () =>
      proctorClientRef.current!.myExamPillars({
        includeQuestionResults,
        excludeAssignments,
      }),
    {
      refetchOnMount: true,
      enabled,
    }
  );

  const data = useMemo((): UserExamsData | null => {
    if (!sections || !examsResponse) return null;

    const exams = examsResponse.exams ?? [];
    const pillars = pillarsResponse?.pillars ?? [];

    // Index pillars by path for O(1) lookup
    const pillarsByPath = new Map<string, ExamPillar>();
    for (const pillar of pillars) {
      if (pillar.pillarPath) {
        pillarsByPath.set(pillar.pillarPath, pillar);
      }
    }

    let activeExam: UserExamsData["activeExam"] = null;

    const sectionData: SectionExamData[] = sortExamSections(sections)
      .filter((s): s is ExamSection & { path: string } => !!s.path)
      .map(section => {
        const sectionPath = section.path;

        // Find exams whose topicPrefixes include this section.
        // Assigned exams may span multiple sections — they still match here
        // and will appear under each relevant section.
        const sectionExams = exams.filter(exam =>
          exam.examConfig?.filters?.topicPrefixes?.includes(sectionPath)
        );

        const inProgressExam = sectionExams.find(e => e.status === "started") ?? null;

        // Track first in-progress exam across all sections
        if (inProgressExam && !activeExam) {
          activeExam = { exam: inProgressExam, section };
        }

        // Match pillar scores to this section
        const pillar = pillarsByPath.get(sectionPath);

        return {
          section,
          inProgressExam,
          latestScorePercent: pillar?.latestScorePercent ?? null,
          latestTeamAveragePercent: pillar?.latestTeamAveragePercent ?? null,
          timesTaken: pillar?.timesTaken ?? 0,
          scores: pillar?.previousScores ?? [],
          topicScores: pillar?.topicScores ?? [],
          questionResults: pillar?.questionResults ?? null,
          latestScoreCompletedAt: pillar?.latestScoreCompletedAt ?? null,
        };
      });

    return { sections: sectionData, activeExam };
  }, [sections, examsResponse, pillarsResponse]);

  const refetch = async () => {
    await Promise.all([sectionsRefetch(), examsRefetch(), pillarsRefetch()]);
  };

  const error = sectionsError || examsError || pillarsError;
  const isLoading = sectionsLoading || examsLoading || pillarsLoading;
  const isFetching = sectionsFetching || examsFetching || pillarsFetching;

  return { data, isLoading, error, refetch, isFetching };
};
